/**
 * ConversationSidebar — 260px expanded panel for the Hybrid sidebar.
 *
 * Layout (top → bottom):
 *   1. Header row: [+ Nuova chat] primary CTA + collapse button (◀)
 *   2. "Progetti" eyebrow — links to the projects page, "+" creates one.
 *      ANCHORED outside the scroller, so the scrollbar belongs to the chat
 *      list alone.
 *   3. "Fissate" group (if any conversation is pinned via sidebarStore)
 *   4. "Chat recenti" — ONE flat lane holding every other conversation,
 *      recency-desc. The sidebar deliberately does not split chats per
 *      project any more: projects live on their own page, and a filed chat
 *      keeps showing up here instead of vanishing from the history.
 *
 * Sibling: `ConversationMiniRail` (48px). Parent (`_authed/route.tsx`) picks
 * which one to mount based on `useSidebarStore().isExpanded`.
 *
 * Mutations consumed (all DRY — no new endpoints invented):
 *   - `useCreateConversation`           → "Nuova chat"
 *   - `useDeleteConversation`           → "Elimina" row action
 *   - `useUpdateConversation`           → "Rinomina" row action (PATCH
 *                                          /conversations/{user}/{id}, with
 *                                          optimistic local mirror + rollback)
 *   - `useSidebarStore.togglePinned`    → "Fissa / Sfissa"
 *   - `useAssignConversationToProject`  → "Sposta in progetto" / "Cambia progetto"
 *   - `useUnassignConversationFromProject` → "Rimuovi da progetto"
 *                                          (shown only when the row already
 *                                          carries a `project_id`)
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import type { Project } from "@ando/core/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  toast,
} from "@ando/ui";
import { confirm, prompt } from "@ando/ui/dialog";
import { Link, useNavigate } from "@tanstack/react-router";

import {
  Archive,
  ArrowUpRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Folder,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Share2,
  Trash2,
} from "lucide-react";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import { useShareDialogStore } from "@/features/share/state/shareDialogStore";

import {
  NewProjectDialog,
  ProjectIcon,
  useArchiveProject,
  useAssignConversationToProject,
  useUnassignConversationFromProject,
  useUpdateProject,
} from "@/features/projects";
import { ACTIVITY_DOT, classifyActivity } from "@/features/projects/utils/activity";
import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import {
  useCreateConversation,
  useDeleteConversation,
  useUpdateConversation,
} from "../hooks/useConversationMutations";
import { useConversationsQuery } from "../hooks/useConversationsQuery";
import { useSidebarProjects } from "../hooks/useSidebarProjects";
import {
  UNASSIGNED_PROJECT_KEY,
  groupConversationsByProject,
} from "../lib/groupConversationsByProject";
import { selectCurrentConversationId, useChatStore } from "../state/chatStore";
import {
  selectCollapsedProjects,
  selectPinnedConversations,
  useSidebarStore,
} from "../state/sidebarStore";
import type { ChatConversation } from "../state/types";

export interface ConversationSidebarProps {
  /** Called when the user opens the Cmd+K palette via header trigger (none yet — placeholder). */
  onOpenPalette?: () => void;
  /**
   * Override the default "select conversation" behavior. When the sidebar
   * is rendered as an overlay (route shell), the parent passes this so the
   * row click can ALSO close the overlay (UX: see the loaded chat
   * immediately). Falls back to `selectConversation` from the store when
   * absent (preserves the panel-only mount path).
   */
  onSelect?: (id: string) => void;
  /**
   * Called when the user dismisses the sidebar from inside (header chevron).
   * Falls back to the store's `toggleExpanded` so existing call sites that
   * don't pass `onClose` keep the legacy behavior.
   */
  onClose?: () => void;
  /**
   * Embedded mode (Jul 2026): render ONLY the conversation list (projects +
   * threads), with no own chrome — the header (New chat), Search and background
   * are provided by the host unified sidebar as ``[icon][label]`` rows. Lets the
   * collapsible bar reuse all the list logic without duplicating it.
   */
  embedded?: boolean;
}

export const ConversationSidebar: FC<ConversationSidebarProps> = ({
  onSelect,
  onClose,
  embedded = false,
}) => {
  const { i18n } = useLingui();
  // Sidebar UX state — collapse + pinned.
  const toggleExpanded = useSidebarStore((s) => s.toggleExpanded);
  const collapsedProjects = useSidebarStore(selectCollapsedProjects);
  const pinnedMap = useSidebarStore(selectPinnedConversations);
  const toggleProjectCollapsed = useSidebarStore((s) => s.toggleProjectCollapsed);

  // Conversation list — TanStack Query hydrates the store; we read via the
  // store so any WS-driven update (title rename, new message) re-renders.
  useConversationsQuery();
  const conversations = useChatStore((s) => s.conversations);
  const orderedIds = useChatStore((s) => s.orderedIds);
  const currentId = useChatStore(selectCurrentConversationId);
  const storeSelectConversation = useChatStore((s) => s.selectConversation);
  // Wrap so the overlay-aware parent can intercept (close-on-select). The
  // wrapper preserves the legacy panel-only mount path: when `onSelect` is
  // undefined we fall straight through to the store mutation.
  const selectConversation = (id: string): void => {
    if (onSelect) onSelect(id);
    else storeSelectConversation(id);
  };
  const closeSidebar = (): void => {
    if (onClose) onClose();
    else toggleExpanded();
  };

  // Projects — bridge hook returns [] until the parallel projects feature
  // lands. The grouping helper handles an empty array by lumping everyone
  // into the "Senza progetto" bucket.
  const { projects } = useSidebarProjects();

  const { create } = useCreateConversation();
  const { remove } = useDeleteConversation();
  const assignToProject = useAssignConversationToProject();

  // "Nuova chat" from a project's ⋯ menu: create a thread and drop it
  // straight into that project, then open it.
  const handleNewChatInProject = (projectId: string | null): void => {
    const id = create();
    if (projectId) assignToProject.mutate({ projectId, conversationId: id });
    selectConversation(id);
  };

  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const projectsCollapsed = collapsedProjects[PROJECTS_GROUP_KEY] === true;

  // Ordered conversation list — `orderedIds` is recency-desc per the
  // conversations slice contract. Title filtering used to live here, wired
  // to a search field that has not rendered since the rail started mounting
  // this panel as ``embedded``: dead state feeding a dead input. Searching
  // is the palette's job (documents + chats + actions), reached from the
  // rail's magnifier.
  const filteredConversations = useMemo<ChatConversation[]>(
    () =>
      orderedIds.map((id) => conversations[id]).filter((c): c is ChatConversation => Boolean(c)),
    [orderedIds, conversations],
  );

  // Pinned first, then the split that production always had: a chat lives
  // EITHER under its project EITHER in the loose lane below — never in both,
  // so no conversation is printed twice in the same sidebar.
  const pinned = useMemo(
    () => filteredConversations.filter((c) => pinnedMap[c.id] === true),
    [filteredConversations, pinnedMap],
  );
  const unpinned = useMemo(
    () => filteredConversations.filter((c) => pinnedMap[c.id] !== true),
    [filteredConversations, pinnedMap],
  );

  const groups = useMemo(
    () => groupConversationsByProject(unpinned, projects),
    [unpinned, projects],
  );
  /** Project groups — including the empty ones: a project you just created
   *  has to be visible before any chat lives inside it. */
  const projectGroups = useMemo(() => groups.filter((g) => g.project !== null), [groups]);
  /** Chats that belong to no project — the "Chat recenti" lane. */
  const looseChats = useMemo(
    () => groups.find((g) => g.key === UNASSIGNED_PROJECT_KEY)?.conversations ?? [],
    [groups],
  );

  const onNewChat = useCallback((): void => {
    const id = create();
    selectConversation(id);
  }, [create, selectConversation]);

  // ⌘N / Ctrl+N → new chat (the kbd hint on the button is functional).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        onNewChat();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNewChat]);

  const metaGlyph = useMemo(
    () =>
      typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
        ? "⌘"
        : "Ctrl ",
    [],
  );

  return (
    <aside
      aria-label={i18n._(msg`Cronologia conversazioni`)}
      className={cn(
        "flex h-full w-full min-w-0 shrink-0 flex-col",
        // Opaque in standalone mode; transparent when embedded (the host
        // unified bar owns the background).
        embedded ? "" : "bg-cream",
      )}
    >
      {!embedded ? (
        <>
          {/* ── Header: New chat CTA + collapse arrow ──────────────────────── */}
          <div className="relative flex items-center justify-center border-hairline border-b px-3 py-2">
            <button
              type="button"
              onClick={onNewChat}
              title={`${i18n._(msg`Nuova chat`)} · ${metaGlyph}N`}
              aria-label={i18n._(msg`Nuova chat`)}
              aria-keyshortcuts="Meta+N Control+N"
              className={cn(
                "group flex shrink-0 items-center gap-2.5 rounded-full px-2.5 py-1.5",
                "text-ando-orange transition-colors duration-200 hover:bg-ando-orange/[0.07]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
              )}
            >
              {/* Stylised pencil-line blob — orange OUTLINE (no fill) that morphs
              organically, and comes alive (scales + glows) on hover, recalling
              the login blob. CSS-only → light + reduced-motion safe. */}
              <span
                aria-hidden
                className={cn(
                  "block h-8 w-8 border-[1.7px] border-current",
                  "animate-blob-morph [animation-play-state:paused] motion-reduce:animate-none motion-reduce:rounded-[42%]",
                  "transition-[box-shadow] duration-300",
                  "group-hover:shadow-[0_0_16px_rgba(255,122,26,0.5)] group-hover:[animation-play-state:running]",
                )}
              />
              <span className="whitespace-nowrap font-medium text-[13px] text-ando-orange">
                <Trans>Nuova chat</Trans>
              </span>
              <Plus
                aria-hidden
                className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:rotate-90"
              />
            </button>
            <button
              type="button"
              onClick={closeSidebar}
              aria-label={i18n._(msg`Chiudi sidebar`)}
              className={cn(
                "-translate-y-1/2 absolute top-1/2 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md",
                "text-ink-2 transition-colors hover:bg-black/[0.04] hover:text-ink-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
              )}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>
        </>
      ) : null}

      {/* ── Scrollable list ────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {/* ── Progetti ─────────────────────────────────────────────────
              Inside the scroller, with the chats: anchoring it above meant a
              capped block that scrolled on its own, so the sidebar had two
              nested scroll areas. One list, one scrollbar. The accordion
              (state persisted like every other group) is what keeps it from
              crowding the chats when you don't want it.
          */}
        <div className="px-0 pt-1 pb-1">
          <div className="group/projects flex items-center gap-1">
            <button
              type="button"
              onClick={() => toggleProjectCollapsed(PROJECTS_GROUP_KEY)}
              aria-expanded={!projectsCollapsed}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-1 rounded-md px-2 py-1.5 text-left",
                // A heading has to outweigh its own body copy: this was 10px
                // ink-3 above an 11px sentence — smaller and fainter than the
                // text it was introducing.
                // Shared eyebrow type: same size, same ink jump as the lanes
                // below (see SidebarGroup) — two headings at two weights read
                // as two levels of hierarchy that do not exist.
                "font-semibold text-[11px] text-ink-3 uppercase tracking-wider",
                // No wash here: an eyebrow is a label, and a filled band across
                // the top of the list shouted louder than the rows below it.
                // It brightens instead, ink-3 → ink-1: the widest jump the
                // scale allows, so the hover is unmistakable.
                "transition-colors hover:text-ink-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
              )}
            >
              {/* No count: it restated rows the accordion already shows, and
                  the lanes below don't carry one either. */}
              <Trans>Progetti</Trans>
              {projectsCollapsed ? (
                <ChevronRight className="h-3 w-3 shrink-0" />
              ) : (
                <ChevronDown className="h-3 w-3 shrink-0" />
              )}
            </button>
            {/* The page sits behind its own control now that the title owns the
                accordion: a heading that silently navigates is a hidden
                affordance. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/workspace/projects"
                  aria-label={i18n._(msg`Tutti i progetti`)}
                  className={cn(
                    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                    "text-ink-3 transition-[opacity,color] hover:text-ink-1",
                    // Revealed by hovering ANYWHERE on the header row — in both
                    // states, collapsed or open.
                    "opacity-0 focus-visible:opacity-100 group-hover/projects:opacity-100",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                  )}
                >
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <Trans>Tutti i progetti</Trans>
              </TooltipContent>
            </Tooltip>
            <button
              type="button"
              onClick={() => setNewProjectOpen(true)}
              aria-label={i18n._(msg`Nuovo progetto`)}
              className={cn(
                "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                // Same quiet treatment as the ↗ next to it — two adjacent icons
                // must not answer the pointer in two different ways.
                "text-ink-3 transition-colors hover:text-ink-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
              )}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>

          {projectsCollapsed ? null : projects.length === 0 ? (
            // Zero projects used to hide this row entirely "to keep the sidebar
            // quiet" — which hid the ONLY button that creates the first one.
            <p className="px-2 pb-1 text-[11px] text-ink-3 leading-relaxed">
              <Trans>
                Nessun progetto. Un progetto tiene insieme istruzioni, dati e memoria di un lavoro.
              </Trans>
            </p>
          ) : (
            <div>
              {projectGroups.map((g) => {
                const project = g.project;
                if (!project) return null;
                const shown = g.conversations.slice(0, PROJECT_CHATS_PREVIEW);
                const hasMore = g.conversations.length > shown.length;
                return (
                  <SidebarGroup
                    key={g.key}
                    label={project.name}
                    count={g.conversations.length}
                    collapsed={collapsedProjects[g.key] === true}
                    onToggle={() => toggleProjectCollapsed(g.key)}
                    icon={<Folder className="h-3 w-3" />}
                    project={project}
                    onDeleteAll={() => {
                      for (const c of g.conversations) void remove(c.id);
                    }}
                    onNewChat={() => handleNewChatInProject(project.project_id)}
                  >
                    {/* Nested under a hairline guide: a project's chats have to
                        read as INSIDE it, not as siblings of the loose lane
                        below. Capped — the sidebar is a way in, not the whole
                        archive; the rest is one click away on the project.
                        The guide is an OVERLAID line, not the container's
                        border: as a border it fenced the rows to its right, so
                        a selected row's pill started after the line instead of
                        running the full width like every other row. Absolute =
                        it paints over the pill and stays unbroken. */}
                    <div className="relative">
                      <span
                        aria-hidden
                        className="pointer-events-none absolute top-0 bottom-0 left-3.5 w-px bg-hairline"
                      />
                      {shown.map((c) => (
                        <ConversationRow
                          key={c.id}
                          conversation={c}
                          active={c.id === currentId}
                          pinned={false}
                          nested
                          projects={projects}
                          onSelect={() => selectConversation(c.id)}
                          onDelete={() => void remove(c.id)}
                        />
                      ))}
                      {hasMore ? (
                        <Link
                          to="/workspace/projects/$projectId"
                          params={{ projectId: project.project_id }}
                          className={cn(
                            "block rounded-md py-1 pr-2 pl-6 text-[11px] text-ink-3",
                            "transition-colors hover:bg-cream-deep hover:text-ink-1",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                          )}
                        >
                          <Trans>Visualizza tutto</Trans>
                        </Link>
                      ) : null}
                    </div>
                  </SidebarGroup>
                );
              })}
            </div>
          )}
        </div>

        {pinned.length > 0 ? (
          <SidebarGroup
            label={i18n._(msg`Fissate`)}
            count={pinned.length}
            // Counts are gone from every lane now (see "Progetti" above), so
            // this one would have been the last number standing.
            showCount={false}
            collapsed={collapsedProjects.__pinned__ === true}
            onToggle={() => toggleProjectCollapsed("__pinned__")}
            icon={<Pin className="h-3 w-3" />}
          >
            {pinned.map((c) => (
              <ConversationRow
                key={c.id}
                conversation={c}
                active={c.id === currentId}
                pinned
                projects={projects}
                onSelect={() => selectConversation(c.id)}
                onDelete={() => void remove(c.id)}
              />
            ))}
          </SidebarGroup>
        ) : null}

        {looseChats.length === 0 && pinned.length === 0 && projectGroups.length === 0 ? (
          <div className="px-2 py-6 text-center text-[11px] text-ink-3">
            <Trans>Nessuna conversazione ancora</Trans>
          </div>
        ) : null}

        {looseChats.length > 0 ? (
          <SidebarGroup
            label={i18n._(msg`Chat recenti`)}
            count={looseChats.length}
            showCount={false}
            collapsed={collapsedProjects.__recents__ === true}
            onToggle={() => toggleProjectCollapsed("__recents__")}
            onDeleteAll={() => {
              for (const c of looseChats) void remove(c.id);
            }}
          >
            {looseChats.map((c) => (
              <ConversationRow
                key={c.id}
                conversation={c}
                active={c.id === currentId}
                pinned={false}
                projects={projects}
                onSelect={() => selectConversation(c.id)}
                onDelete={() => void remove(c.id)}
              />
            ))}
          </SidebarGroup>
        ) : null}
      </div>

      {/* New project dialog — mounted at the sidebar root so its portal
          stays inside the workspace tree (matches the v2 modal stacking
          contract). Controlled state lives next to the trigger above. */}
      <NewProjectDialog open={newProjectOpen} onOpenChange={setNewProjectOpen} />
    </aside>
  );
};

ConversationSidebar.displayName = "ConversationSidebar";

// ───────────────────────────────────────────────────────────────────────────
// SidebarGroup — collapsible header + body
// ───────────────────────────────────────────────────────────────────────────

/** Collapse key for the projects accordion — shares the persisted map with
 *  the conversation groups, so its open/closed state survives a reload like
 *  everything else in this sidebar. */
const PROJECTS_GROUP_KEY = "__projects__";

/** How many chats a project shows inline before deferring to its page. */
const PROJECT_CHATS_PREVIEW = 5;

interface SidebarGroupProps {
  label: string;
  count: number;
  /** Whether to PRINT the count. The recents lane hides it (it only restated
   *  rows already on screen) but still needs the number: it feeds the
   *  "Eliminare N chat?" confirm and gates the ⋯ menu. */
  showCount?: boolean;
  collapsed: boolean;
  onToggle: () => void;
  /** Group glyph. `null` on the recents lane — it is not a folder. */
  icon?: React.ReactNode;
  /** Project metadata when this is a real project group (not pinned / unassigned). */
  project?: Project | null;
  /** Bulk-delete handler. When provided, a "…" menu appears on the group
   *  header with an "Elimina tutte" item that prompts confirm and fires this
   *  callback. Used on project + unassigned groups, omitted on Pinned. */
  onDeleteAll?: () => void;
  /** Create a new chat inside this project (shown in the ⋯ menu). */
  onNewChat?: () => void;
  children: React.ReactNode;
}

const SidebarGroup: FC<SidebarGroupProps> = ({
  label,
  count,
  showCount = true,
  collapsed,
  onToggle,
  icon,
  project,
  onDeleteAll,
  onNewChat,
  children,
}) => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const updateMutation = useUpdateProject();
  const archiveMutation = useArchiveProject();

  // Discriminator — a "rich" project group renders the project's own
  // icon + heartbeat + overdue badge + clickable name (links to the
  // ProjectDetailPage). The Pinned and Senza-progetto buckets fall
  // through to the legacy quiet chrome.
  const isRichProject = !!project;
  const activity = isRichProject ? classifyActivity(project.last_active_at) : null;
  const overdue = isRichProject ? (project.overdue_tasks_count ?? 0) : 0;

  const handleBulkDelete = async (): Promise<void> => {
    setMenuOpen(false);
    if (!onDeleteAll || count === 0) return;
    const confirmed = await confirm({
      title: i18n._(msg`Eliminare tutte le chat?`),
      description: i18n._(
        msg`Tutte le chat di "${label}" verranno rimosse. L'azione non è reversibile.`,
      ),
      confirmLabel: i18n._(msg`Elimina tutte`),
      destructive: true,
    });
    if (confirmed) onDeleteAll();
  };

  const handleOpenProject = (): void => {
    if (!project) return;
    setMenuOpen(false);
    void navigate({
      to: "/workspace/projects/$projectId",
      params: { projectId: project.project_id },
    });
  };

  const handleRenameProject = async (): Promise<void> => {
    if (!project) return;
    setMenuOpen(false);
    const next = await prompt({
      title: i18n._(msg`Rinomina progetto`),
      label: i18n._(msg`Nuovo nome`),
      initialValue: project.name,
      minLength: 1,
    });
    if (next === null) return;
    const trimmed = next.trim();
    if (trimmed.length === 0 || trimmed === project.name) return;
    updateMutation.mutate({
      projectId: project.project_id,
      patch: { name: trimmed },
    });
  };

  const handleArchiveProject = async (): Promise<void> => {
    if (!project) return;
    setMenuOpen(false);
    const confirmed = await confirm({
      title: i18n._(msg`Archiviare "${project.name}"?`),
      description: i18n._(
        msg`Il progetto verrà spostato in archivio. Le conversazioni resteranno nella cronologia, senza progetto associato.`,
      ),
      confirmLabel: i18n._(msg`Archivia`),
      destructive: true,
    });
    if (confirmed) archiveMutation.mutate(project.project_id);
  };

  // Menu shows when there's at least one actionable verb: project verbs
  // (open / rename / archive) or the bulk-delete callback on non-empty
  // groups. The Senza-progetto bucket therefore keeps just the legacy
  // "Elimina tutte" entry.
  const hasMenu = isRichProject || (!!onDeleteAll && count > 0);

  return (
    <div className="group/group mb-1">
      <div
        className={cn(
          "flex w-full items-center gap-1 rounded-md px-1.5 py-1.5",
          // Warm paper, not cold black: a neutral grey wash on a cream ground
          // reads as a stain. `cream-deep` is the system's "one step deeper
          // paper" token — same hover across every row of this list.
          "hover:bg-cream-deep",
        )}
      >
        {isRichProject ? (
          // ── Rich project header: the ROW toggles, the ↗ opens the page.
          // No chevron (the click target is the row itself) and no orange on
          // the name: the accent belongs to Ando and to actions, not to a
          // navigation row you merely hover.
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? i18n._(msg`Espandi ${label}`) : i18n._(msg`Comprimi ${label}`)}
            className={cn(
              // `flex-1`: the toggle target is the WHOLE row, not just the
              // words. Only the two trailing icons keep their own actions,
              // so there is no dead strip that looks clickable and isn't.
              "flex min-w-0 flex-1 items-center gap-1.5 rounded py-0.5 text-left",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            )}
          >
            <ProjectIcon
              iconName={project.icon}
              colorToken={project.color}
              size={11}
              hideDot
              className="h-4 w-4 shrink-0"
            />
            <span className="truncate font-medium text-[12px] text-ink-1">{project.name}</span>
            {activity ? (
              <span
                aria-hidden
                title={i18n._(msg`Ultima attività su ${project.name}`)}
                className={cn("h-1.5 w-1.5 shrink-0 rounded-full", ACTIVITY_DOT[activity])}
              />
            ) : null}
            {overdue > 0 ? (
              <span
                aria-label={
                  overdue === 1
                    ? i18n._(msg`1 attività in ritardo`)
                    : i18n._(msg`${overdue} attività in ritardo`)
                }
                className={cn(
                  "inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full px-1",
                  "font-semibold text-[9px] text-white",
                  "bg-rose-500",
                )}
              >
                {overdue}
              </span>
            ) : null}
          </button>
        ) : (
          // ── Legacy quiet chrome (Pinned, Senza progetto)
          <button
            type="button"
            onClick={onToggle}
            className={cn(
              "flex min-w-0 items-center gap-1.5 text-left",
              // Same eyebrow type as the anchored "Progetti" heading above:
              // 11px and the same ink-3 → ink-1 hover. They are peers.
              "font-semibold text-[11px] text-ink-3 uppercase tracking-wider",
              "transition-colors hover:text-ink-1",
            )}
          >
            {icon ? (
              <span aria-hidden className="text-ando-orange">
                {icon}
              </span>
            ) : null}
            <span className="truncate">{label}</span>
          </button>
        )}

        {/* Chevron AFTER the title — and only on the plain lanes: a project
            row toggles by clicking the row itself, so a second control for
            the same action would be noise. */}
        {isRichProject ? null : (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? i18n._(msg`Espandi ${label}`) : i18n._(msg`Comprimi ${label}`)}
            className={cn(
              "inline-flex h-5 w-4 shrink-0 items-center justify-center rounded",
              "text-ink-3 hover:text-ink-1",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            )}
          >
            {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        )}

        <span className="flex-1" aria-hidden />
        {/* Where the chat counter used to be: a number the user could already
            see by expanding. The slot now carries the way INTO the project. */}
        {isRichProject ? (
          <Link
            to="/workspace/projects/$projectId"
            params={{ projectId: project.project_id }}
            aria-label={i18n._(msg`Apri progetto: ${project.name}`)}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded",
              "text-ink-3 transition-[opacity,color] hover:text-ink-1",
              // Quiet at rest like the ⋯ beside it: the row should read as a
              // name, not as a strip of controls.
              "opacity-0 focus-visible:opacity-100 group-hover/group:opacity-100",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            )}
          >
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        ) : showCount ? (
          <span className="shrink-0 text-[10px] text-ink-4 tabular-nums">{count}</span>
        ) : null}

        {hasMenu ? (
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={i18n._(msg`Azioni gruppo ${label}`)}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
                className={cn(
                  "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded",
                  "text-ink-3 transition-opacity",
                  // Hide-on-rest, reveal-on-hover/focus/open for a quiet header
                  menuOpen
                    ? "opacity-100"
                    : "opacity-0 focus-visible:opacity-100 group-hover/group:opacity-100",
                  "hover:bg-black/[0.06] hover:text-ink-1",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                )}
              >
                <MoreHorizontal className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={4}
              className="w-[220px] p-1"
              onClick={(e) => e.stopPropagation()}
            >
              {isRichProject ? (
                <>
                  {onNewChat ? (
                    <>
                      <MenuItem
                        onClick={() => {
                          setMenuOpen(false);
                          onNewChat();
                        }}
                        icon={<Plus className="h-3 w-3" />}
                      >
                        <Trans>Nuova chat</Trans>
                      </MenuItem>
                      <div className="my-1 h-px bg-hairline" aria-hidden />
                    </>
                  ) : null}
                  <MenuItem onClick={handleOpenProject} icon={<ArrowUpRight className="h-3 w-3" />}>
                    <Trans>Apri progetto</Trans>
                  </MenuItem>
                  <MenuItem
                    onClick={() => void handleRenameProject()}
                    icon={<Pencil className="h-3 w-3" />}
                  >
                    <Trans>Rinomina</Trans>
                  </MenuItem>
                  <MenuItem
                    onClick={() => void handleArchiveProject()}
                    icon={<Archive className="h-3 w-3" />}
                  >
                    <Trans>Archivia</Trans>
                  </MenuItem>
                  {onDeleteAll && count > 0 ? (
                    <>
                      <div className="my-1 h-px bg-hairline" aria-hidden />
                      <MenuItem
                        onClick={() => void handleBulkDelete()}
                        icon={<Trash2 className="h-3 w-3" />}
                        destructive
                      >
                        <Trans>Elimina tutte le chat ({count})</Trans>
                      </MenuItem>
                    </>
                  ) : null}
                </>
              ) : (
                <MenuItem
                  onClick={() => void handleBulkDelete()}
                  icon={<Trash2 className="h-3 w-3" />}
                  destructive
                >
                  <Trans>Elimina tutte ({count})</Trans>
                </MenuItem>
              )}
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
      {/* Accordion body — animated with a GRID TRACK (0fr → 1fr) rather than a
          height. It eases in BOTH directions without measuring anything, so
          there is no stored height to fall out of sync when a row is added or
          a title wraps; a `max-height` guess would either clip long lists or
          coast through empty space on close. Same 200ms curve as the panel.

          `inert` while collapsed: the rows stay mounted for the animation, and
          an `overflow-hidden` box alone would still let Tab walk into content
          nobody can see. `inert` takes the subtree out of the tab order AND
          the accessibility tree in one attribute (React 19 passes it through).

          `gap-0.5`, not `gap-px`: a hovered row next to the selected one used
          to fuse into a single grey slab; two pixels of cream keeps them two
          rows. */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-[cubic-bezier(0.2,0.7,0.2,1)]",
          collapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
        )}
      >
        <div className="overflow-hidden" inert={collapsed}>
          <div className="mt-0.5 flex flex-col gap-0.5">{children}</div>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// ConversationRow — single chat entry
// ───────────────────────────────────────────────────────────────────────────

interface ConversationRowProps {
  conversation: ChatConversation;
  active: boolean;
  pinned: boolean;
  /** Rendered inside a project's nested block: adds the ring marker that
   *  makes the row read as a child of the project above it. */
  nested?: boolean;
  /** Project list for the inline "Sposta in progetto" sub-menu. */
  projects: readonly Project[];
  onSelect: () => void;
  onDelete: () => void;
}

const ConversationRow: FC<ConversationRowProps> = ({
  conversation,
  active,
  pinned,
  nested = false,
  projects,
  onSelect,
  onDelete,
}) => {
  const { i18n } = useLingui();
  const togglePinned = useSidebarStore((s) => s.togglePinned);
  const assignMutation = useAssignConversationToProject();
  const unassignMutation = useUnassignConversationFromProject();
  const updateMutation = useUpdateConversation();
  const openShareDialog = useShareDialogStore((s) => s.open);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProjectsSubmenu, setShowProjectsSubmenu] = useState(false);
  const currentProjectId = conversation.project_id ?? null;
  const isAssignedToProject = currentProjectId !== null;

  const display =
    conversation.title.trim().length > 0 ? conversation.title : i18n._(msg`Senza titolo`);
  const time = formatRelativeTime(conversation.updated_at);

  const handleRename = async (): Promise<void> => {
    setMenuOpen(false);
    // Persist via PATCH /conversations/{user}/{id} — the mutation hook
    // optimistically updates the local store AND rolls back on error.
    // Before June 8, 2026 this was a pure ``setTitle`` store mutation
    // that vanished on refresh; the BE endpoint always existed, the FE
    // just never wired it.
    const next = await prompt({
      title: i18n._(msg`Rinomina conversazione`),
      label: i18n._(msg`Nuovo titolo`),
      initialValue: conversation.title,
      minLength: 1,
    });
    if (next === null) return;
    const trimmed = next.trim();
    if (trimmed.length === 0 || trimmed === conversation.title) return;
    try {
      await updateMutation.update(conversation.id, { title: trimmed });
    } catch (err) {
      // The hook already rolled back the optimistic local update.
      // Surface a toast so the user knows the rename didn't stick.
      toast.error(i18n._(msg`Rinomina non riuscita`), {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleAssignProject = (projectId: string): void => {
    setMenuOpen(false);
    setShowProjectsSubmenu(false);
    // If the row already lives under another project, assigning replaces it
    // — the BE endpoint is upsert-style so we don't need a separate unassign
    // step. Same-project re-click is a no-op at the BE layer.
    if (projectId === currentProjectId) return;
    assignMutation.mutate({ projectId, conversationId: conversation.id });
  };

  const handleUnassignProject = (): void => {
    setMenuOpen(false);
    if (currentProjectId === null) return;
    unassignMutation.mutate({
      projectId: currentProjectId,
      conversationId: conversation.id,
    });
  };

  const handleExport = (): void => {
    setMenuOpen(false);
    // Re-uses the slash-command `/export` pipeline indirectly — the route
    // already mounts `useExportConversation`; calling it from a non-React
    // context is messy. The Command Palette covers this action; keeping the
    // row-level entry as a TODO until we lift the mutation up.
  };

  const handleShare = (): void => {
    setMenuOpen(false);
    openShareDialog({ share_scope: "conversation", conversation_id: conversation.id });
  };

  return (
    <div
      className={cn(
        // 36px tall with a uniform 6px pad: the 24px control inside then
        // clears every side by exactly 6px, which is what makes the radius
        // maths unambiguous (10 − 6 = 4 for the control). Before, the pad was
        // 8px at the side and ~5.5px above/below — no single "padding"
        // existed to compute from.
        "group relative flex h-9 items-center gap-2 rounded-md px-1.5",
        "transition-colors hover:bg-cream-deep",
        // Selected = one step deeper than the hover paper, still neutral.
        // Orange here made the whole screen read as accent; state in the
        // navigation chrome is neutral by rule, the accent is reserved for
        // Ando itself and for actions. Two traps also fixed on the way:
        // `bg-ando-orange-soft/40` was NOT 40% of a soft tint (Tailwind
        // REPLACES the alpha of `rgba(255,122,26,0.12)`, so it painted 40%
        // orange), and the old `border-l-2` ate 2px off the content box, so
        // a selected row sat 2px right of every other one.
        // Selected must out-weigh hover by a visible margin: at 6% it
        // darkened the cream by ~13 levels against hover's ~8 — a gap nobody
        // can see, so a hovered row read as selected. 10% doubles the
        // distance while staying a neutral wash, not a slab.
        active && "bg-ink-1/[0.10] hover:bg-ink-1/[0.13]",
      )}
    >
      {/*
       * Full-row selection button — sits behind the visual content so the
       * entire row click target is one real <button> (a11y P0). The trailing
       * menu button is rendered AFTER with higher stacking so its own
       * pointer events win.
       */}
      <button
        type="button"
        onClick={onSelect}
        aria-label={i18n._(msg`Apri conversazione: ${display}`)}
        aria-current={active ? "true" : undefined}
        className={cn(
          // inset-0 → same corner, same radius as the row above.
          "absolute inset-0 rounded-md",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-inset",
        )}
      />

      <div
        className={cn(
          "pointer-events-none relative flex min-w-0 flex-1 items-center gap-2",
          // Offsets are measured from the ROW's padding edge now, so they
          // subtract the 6px the row already provides: the text lands on the
          // same x as before (12px top-level, 24px nested — the latter
          // clearing the overlaid guide line at 14px).
          nested ? "ml-[18px]" : "ml-1.5",
        )}
      >
        {nested ? (
          // Ring marker: the row reads as an item OF the project above it,
          // not as one more chat in the same column.
          <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full border border-ink-4" />
        ) : null}
        {pinned ? <Pin className="h-2.5 w-2.5 shrink-0 text-ando-orange" aria-hidden /> : null}
        {/* Selection is carried by the type ladder + the accent now that the
            initial chip is gone: the chip was decoration (`aria-hidden`, the
            first letter of the title repeated two centimetres from the
            title), but it was also half the active signal. */}
        <span
          className={cn(
            "truncate text-[13px]",
            active ? "font-semibold text-ink-1" : "font-medium text-ink-2",
          )}
        >
          {display}
        </span>
      </div>

      {/* Date and ⋯ share ONE right-aligned cell: they swap on hover, so any
          other arrangement makes the row twitch between two anchors. The date
          used to sit inside the title block, ending ~32px short of where the
          menu appears. */}
      <div className="relative flex h-6 w-[38px] shrink-0 items-center justify-end">
        {time.length > 0 ? (
          // `tabular-nums` so the digits line up down the list instead of
          // jittering. `ink-3`, not `ink-4`: the faintest step is flagged
          // decor-only in the preset (~1.75:1) and a date is information.
          // Centred on the ⋯ box, not right-aligned to the row: the glyph is
          // centred in its 24px control, so a right-aligned date hung ~12px
          // further out and the swap on hover slid sideways. Same centre =
          // clean cross-fade. It overflows the 24px box symmetrically, which
          // is why the cell around it stays wider.
          <span className="pointer-events-none absolute right-0 flex h-6 w-6 items-center justify-center whitespace-nowrap text-[10px] text-ink-3 tabular-nums transition-opacity group-hover:opacity-0">
            {time}
          </span>
        ) : null}

        {/* Action menu — always above the full-row select button (relative z) */}
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={i18n._(msg`Azioni conversazione`)}
              className={cn(
                // Concentric radii — inner = outer − padding, and the padding
                // that binds is the TIGHT one: the row is ~35px tall around a
                // 24px control, so ~5.5px above and below (8px at the side).
                // 10 − 5.5 ≈ 4. At the row's own 10px the control read
                // rounder than the box holding it.
                "absolute right-0 inline-flex h-6 w-6 items-center justify-center rounded-xs",
                "text-ink-3 transition-opacity hover:bg-black/[0.06] hover:text-ink-1",
                "opacity-0 focus-visible:opacity-100 group-hover:opacity-100",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                menuOpen && "opacity-100",
              )}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={4}
            className="w-52 p-1"
            onClick={(e) => e.stopPropagation()}
          >
            {showProjectsSubmenu ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowProjectsSubmenu(false)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[11px]",
                    "text-ink-3 hover:bg-black/[0.04] hover:text-ink-1",
                  )}
                >
                  <ChevronLeft className="h-3 w-3" />
                  <span>
                    <Trans>Indietro</Trans>
                  </span>
                </button>
                <div className="my-1 h-px bg-hairline" aria-hidden />
                {projects.length === 0 ? (
                  <div className="px-2 py-3 text-center text-[11px] text-ink-3">
                    <Trans>Nessun progetto</Trans>
                  </div>
                ) : (
                  projects.map((p) => {
                    const isCurrent = p.project_id === currentProjectId;
                    return (
                      <MenuItem
                        key={p.project_id}
                        onClick={() => handleAssignProject(p.project_id)}
                        icon={<Folder className="h-3 w-3" />}
                        active={isCurrent}
                      >
                        {p.name}
                      </MenuItem>
                    );
                  })
                )}
              </>
            ) : (
              <>
                <MenuItem onClick={() => void handleRename()} icon={<Pencil className="h-3 w-3" />}>
                  <Trans>Rinomina</Trans>
                </MenuItem>
                <MenuItem
                  onClick={() => setShowProjectsSubmenu(true)}
                  icon={<Folder className="h-3 w-3" />}
                >
                  {isAssignedToProject ? (
                    <Trans>Cambia progetto</Trans>
                  ) : (
                    <Trans>Sposta in progetto</Trans>
                  )}
                </MenuItem>
                {isAssignedToProject ? (
                  <MenuItem onClick={handleUnassignProject} icon={<Folder className="h-3 w-3" />}>
                    <Trans>Rimuovi da progetto</Trans>
                  </MenuItem>
                ) : null}
                <MenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    togglePinned(conversation.id);
                  }}
                  icon={pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                >
                  {pinned ? <Trans>Sfissa</Trans> : <Trans>Fissa</Trans>}
                </MenuItem>
                <MenuItem onClick={handleShare} icon={<Share2 className="h-3 w-3" />}>
                  <Trans>Condividi</Trans>
                </MenuItem>
                <MenuItem onClick={handleExport} icon={<Folder className="h-3 w-3" />}>
                  <Trans>Esporta</Trans>
                </MenuItem>
                <div className="my-1 h-px bg-hairline" aria-hidden />
                <MenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                  icon={<Trash2 className="h-3 w-3" />}
                  destructive
                >
                  <Trans>Elimina</Trans>
                </MenuItem>
              </>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

interface MenuItemProps {
  onClick: () => void;
  icon: React.ReactNode;
  destructive?: boolean;
  /**
   * When true the item is rendered as the currently-selected entry of its
   * menu (e.g. the project the conversation already belongs to). A subtle
   * orange tint + trailing dot are added — no checkmark to keep the popover
   * scan-friendly. Click stays enabled so re-selecting the same project is
   * a no-op at the BE.
   */
  active?: boolean;
  children: React.ReactNode;
}

const MenuItem: FC<MenuItemProps> = ({ onClick, icon, destructive, active, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-current={active ? "true" : undefined}
    className={cn(
      "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[12px]",
      destructive
        ? "text-rose-600 hover:bg-rose-50"
        : active
          ? "bg-ink-1/[0.10] text-ink-1 hover:bg-ink-1/[0.13]"
          : "text-ink-1 hover:bg-cream-deep",
    )}
  >
    <span
      aria-hidden
      className={destructive ? "text-rose-500" : active ? "text-ando-orange" : "text-ink-3"}
    >
      {icon}
    </span>
    <span className="truncate">{children}</span>
    {active ? (
      <span
        aria-hidden
        className="ml-auto inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ando-orange"
      />
    ) : null}
  </button>
);
